import { useEffect, useState } from 'react';
import { api, ApiError, getConfig, type Issue } from '../api';
import { EMPTY_PLACE_FORM, PlaceForm, type PlaceFields } from '../components/PlaceForm';
import { PlaceClaimForm } from '../components/PlaceClaimForm';
import { Footer, TopBar } from '../components/TopBar';
import { Turnstile } from '../components/Turnstile';

interface Created {
  public_id: string;
  listing_url: string;
  approximate_location: boolean;
}

interface DuplicateHint {
  placeId: string;
  publicId: string;
  name: string;
  claimable: boolean;
}

export function AddPlace() {
  const [siteKey, setSiteKey] = useState<string | null | undefined>(undefined);
  const [token, setToken] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Created | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateHint | null>(null);
  const [claimSent, setClaimSent] = useState(false);

  useEffect(() => {
    document.title = 'Add a local business — The Yard Sale Map';
    getConfig()
      .then((c) => setSiteKey(c.turnstileSiteKey))
      .catch(() => setSiteKey(null));
  }, []);

  async function submit(fields: PlaceFields & { fax_number: string }) {
    setBusy(true);
    setIssues([]);
    setError('');
    setDuplicate(null);
    if (siteKey && !token) {
      setError('Please complete the “verify you are human” check above the button.');
      setBusy(false);
      return;
    }
    try {
      const r = await api.postJson<Created>('/shop-places', {
        ...fields,
        'cf-turnstile-response': token,
      });
      setDone(r);
      window.scrollTo(0, 0);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.code === 'possible_duplicate') {
        setDuplicate({
          placeId: e.data.place_id as string,
          publicId: e.data.public_id as string,
          name: e.data.name as string,
          claimable: Boolean(e.data.claimable),
        });
        setError(e.message);
        window.scrollTo(0, 0);
      } else if (e instanceof ApiError) {
        setIssues(e.issues);
        setError(e.message);
        setResetKey((k) => k + 1);
      } else {
        setError('Something went wrong adding this shop. Please try again.');
        setResetKey((k) => k + 1);
      }
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <>
        <TopBar />
        <main className="page" id="main">
          <h1>Added!</h1>
          <p>It's on the map now. Thanks for helping other treasure-hunters find it.</p>
          {done.approximate_location && (
            <p className="info">
              We could only pin the address to its ZIP center — let us know if it looks off.
            </p>
          )}
          <p>
            <a className="btn primary" href={new URL(done.listing_url).pathname}>
              View it
            </a>
          </p>
          <p style={{ marginTop: 24 }}>
            <a href="/add-place" onClick={() => setDone(null)}>
              Add another shop
            </a>
          </p>
        </main>
        <Footer />
      </>
    );
  }

  if (duplicate) {
    return (
      <>
        <TopBar />
        <main className="page" id="main">
          <h1>Is this already listed?</h1>
          {claimSent ? (
            <p>Thanks — we'll review your request and email you once it's decided.</p>
          ) : duplicate.claimable ? (
            <>
              <p>{error}</p>
              <p>
                <a href={`/place/${duplicate.publicId}`}>{duplicate.name}</a>
              </p>
              <p>
                If it's your shop, let us know so we can put your contact details on the listing.
              </p>
              <PlaceClaimForm placeId={duplicate.placeId} onDone={() => setClaimSent(true)} />
            </>
          ) : (
            <p>{error}</p>
          )}
          <p style={{ marginTop: 24 }}>
            <a
              href="/add-place"
              onClick={() => {
                setDuplicate(null);
                setClaimSent(false);
              }}
            >
              Go back
            </a>
          </p>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <TopBar />
      <main className="page" id="main">
        <h1>Add a local business</h1>
        <p>
          Antique shops, farm stands, bakeries and more — help other shoppers find it on the map,
          and optionally weave it into a route.
        </p>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <PlaceForm
          initial={EMPTY_PLACE_FORM}
          issues={issues}
          busy={busy || siteKey === undefined}
          onSubmit={(f) => void submit(f)}
          extra={
            siteKey !== undefined ? (
              <Turnstile siteKey={siteKey} onToken={setToken} resetKey={resetKey} />
            ) : null
          }
        />
      </main>
      <Footer />
    </>
  );
}
